/*
 * La Lueur variant picker and media sync.
 *
 * Kalles updates variants asynchronously. This file keeps the hidden product
 * form ID in sync and makes the custom Lux gallery follow the selected color
 * by using product.metafields.theme.group_media_obj data rendered on the slides.
 */
(() => {
  'use strict';

  const initializedPickers = new WeakSet();
  const variantData = new WeakMap();
  const fallbackTimers = new WeakMap();

  const normalizeText = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

  const compactText = (value) => normalizeText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '');

  const normalizeColorKey = (value) => {
    const compact = compactText(value);
    const aliases = {
      assorted: 'multi',
      mixed: 'multi',
      multi: 'multi',
      multicolor: 'multi',
      multicolour: 'multi',
      rainbow: 'multi',
      shockingpink: 'fuchsia',
      hotpink: 'fuchsia',
      fuchsia: 'fuchsia',
      black: 'onyx',
      onyx: 'onyx',
      blue: 'midnightblue',
      navy: 'midnightblue',
      navyblue: 'midnightblue',
      midnightblue: 'midnightblue',
      red: 'ruby',
      ruby: 'ruby',
      darkgreen: 'emeraldgreen',
      emerald: 'emeraldgreen',
      emeraldgreen: 'emeraldgreen',
      green: 'emeraldgreen',
      blush: 'rose',
      blushpink: 'rose',
      lightpink: 'rose',
      rose: 'rose'
    };

    return aliases[compact] || compact;
  };

  const colorValuesMatch = (first, second) => {
    const firstText = normalizeText(first);
    const secondText = normalizeText(second);

    if (firstText && firstText === secondText) return true;

    const firstKey = normalizeColorKey(first);
    const secondKey = normalizeColorKey(second);

    return Boolean(firstKey && secondKey && firstKey === secondKey);
  };

  const normalizeImageUrl = (value) => {
    if (!value) return '';

    try {
      const url = new URL(String(value), window.location.origin);
      return url.pathname
        .replace(/(_\d+x\d*|_\d+x|_x\d+|_pico|_icon|_thumb|_small|_compact|_medium|_large|_grande|_master)(?=\.)/i, '')
        .toLowerCase();
    } catch (_) {
      return String(value)
        .split('?')[0]
        .replace(/^https?:/, '')
        .replace(/^\/\//, '')
        .replace(/(_\d+x\d*|_\d+x|_x\d+|_pico|_icon|_thumb|_small|_compact|_medium|_large|_grande|_master)(?=\.)/i, '')
        .toLowerCase();
    }
  };

  const withVariantState = (variant) => {
    if (!variant) return variant;

    return Object.assign({}, variant, {
      variant_state: Object.assign({}, variant.variant_state || {}, {
        sold_out: !variant.available,
        on_sale: Number(variant.compare_at_price) > Number(variant.price),
        pre_order: variant.inventory_management === 'shopify'
          && Number(variant.inventory_quantity) <= 0
          && variant.available
      })
    });
  };

  const parseVariantData = (picker) => {
    if (variantData.has(picker)) return variantData.get(picker);

    const data = picker.querySelector('script[data-ll-variant-data]');
    let variants = [];

    if (data) {
      try {
        variants = JSON.parse(data.textContent);
      } catch (_) {
        variants = [];
      }
    }

    if (!Array.isArray(variants)) variants = [];
    variantData.set(picker, variants);

    return variants;
  };

  const findVariantById = (picker, id) => {
    if (!id) return null;

    return parseVariantData(picker).find((variant) => String(variant.id) === String(id)) || null;
  };

  const getForm = (picker) => {
    const formId = picker && picker.getAttribute('form');
    return formId ? document.getElementById(formId) : null;
  };

  const getOptionFieldsets = (picker) => {
    return Array.from(picker.querySelectorAll('fieldset[data-index]'))
      .sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index))
  };

  const getSelectedOptionElement = (fieldset) => {
    if (!fieldset) return null;

    return fieldset.querySelector('input[is-value][type="radio"]:checked, input[type="radio"]:checked')
      || fieldset.querySelector('hdt-richlist button[aria-selected="true"], button[aria-selected="true"]')
      || fieldset.querySelector('input[is-value][type="hidden"], input[type="hidden"][name^="option"]');
  };

  const getSelectedOptionValues = (picker) => {
    return getOptionFieldsets(picker)
      .map((fieldset) => getSelectedOptionElement(fieldset)?.value || '');
  };

  const getSelectedOptionValueId = (picker, index) => {
    const fieldset = getOptionFieldsets(picker)[index];
    return getSelectedOptionElement(fieldset)?.dataset.valueId || '';
  };

  const getSelectedVariant = (picker) => {
    const values = getSelectedOptionValues(picker);

    if (!values.length || values.some((value) => !value)) return null;

    return parseVariantData(picker).find((variant) => {
      if (!Array.isArray(variant.options)) return false;
      if (variant.options.length !== values.length) return false;

      return variant.options.every((value, index) => value === values[index] || colorValuesMatch(value, values[index]));
    }) || null;
  };

  const updateForm = (picker, variant, notifyTheme = false) => {
    if (!picker || !variant) return false;

    const form = getForm(picker);
    const variantInput = form && form.querySelector('input[name="id"]');

    if (!form || !variantInput) return false;

    const changed = String(variantInput.value) !== String(variant.id) || variantInput.disabled;

    variantInput.value = variant.id;
    variantInput.disabled = false;
    picker.setAttribute('selected-variant', variant.id);

    if (changed) {
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (notifyTheme && changed) {
      form.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: withVariantState(variant), jumpMedia: true }
      }));
    }

    return changed;
  };

  const getPickerFromForm = (form) => {
    if (!form || !form.id) return null;

    try {
      return document.querySelector(`hdt-variant-picker[form="${CSS.escape(form.id)}"]`);
    } catch (_) {
      return document.querySelector(`hdt-variant-picker[form="${form.id}"]`);
    }
  };

  const getPickerFromEvent = (event) => {
    const target = event && event.target;

    if (target?.closest) {
      const picker = target.closest('hdt-variant-picker');
      if (picker) return picker;

      const form = target.closest('form') || (target.tagName === 'FORM' ? target : null);
      const formPicker = getPickerFromForm(form);
      if (formPicker) return formPicker;
    }

    if (target?.tagName === 'FORM') {
      const formPicker = getPickerFromForm(target);
      if (formPicker) return formPicker;
    }

    return document.querySelector('hdt-variant-picker');
  };

  const getVariantFromEvent = (event, picker) => {
    const detail = event && event.detail ? event.detail : {};
    const eventVariant = detail.variant || detail.selectedVariant || detail.currentVariant;

    if (eventVariant && eventVariant.id) {
      return Object.assign({}, findVariantById(picker, eventVariant.id) || {}, eventVariant);
    }

    if (detail.variantId || detail.id) {
      return findVariantById(picker, detail.variantId || detail.id);
    }

    return null;
  };

  const getColorOptionIndex = (picker, outer) => {
    const outerIndexValue = outer?.dataset.colorOptionIndex;

    if (outerIndexValue !== undefined && outerIndexValue !== '') {
      const outerIndex = Number(outerIndexValue);
      if (Number.isInteger(outerIndex) && outerIndex >= 0) return outerIndex;
    }

    const colorFieldset = picker?.querySelector('fieldset.hdt-product-form__input.is-style-color[data-index], fieldset[type*="color"][data-index]');
    const pickerIndex = Number(colorFieldset?.dataset.index);

    return Number.isInteger(pickerIndex) && pickerIndex >= 0 ? pickerIndex : 0;
  };

  const getOptionValue = (picker, index, variant) => {
    if (variant && Array.isArray(variant.options) && variant.options[index]) {
      return variant.options[index];
    }

    const values = picker ? getSelectedOptionValues(picker) : [];
    return values[index] || '';
  };

  const getVariantMediaIds = (variant) => {
    const ids = [
      variant?.mdid,
      variant?.featured_media?.id,
      variant?.featured_image?.id,
      variant?.image?.id,
      variant?.image_id,
      variant?.featured_media_id
    ];

    return new Set(ids.filter((id) => id !== undefined && id !== null && id !== '').map(String));
  };

  const getVariantImageUrls = (variant) => {
    const urls = [
      variant?.featured_media?.preview_image?.src,
      variant?.featured_media?.src,
      variant?.featured_image?.src,
      variant?.featured_image,
      variant?.image?.src,
      variant?.image
    ];

    return new Set(urls.map((url) => normalizeImageUrl(typeof url === 'string' ? url : url?.src)).filter(Boolean));
  };

  const getLuxSlides = (outer) => {
    const track = outer.querySelector('.lux-track');
    const slides = track ? Array.from(track.querySelectorAll('.lux-slide')) : [];
    const realSlides = slides.filter((slide) => !slide.hasAttribute('data-lux-clone'));

    if (realSlides.length) return realSlides;

    if (slides.length <= 2) return slides;

    const firstSrc = normalizeImageUrl(slides[0].dataset.imageSrc || slides[0].querySelector('img')?.src);
    const secondSrc = normalizeImageUrl(slides[1].dataset.imageSrc || slides[1].querySelector('img')?.src);
    const beforeLastSrc = normalizeImageUrl(slides[slides.length - 2].dataset.imageSrc || slides[slides.length - 2].querySelector('img')?.src);
    const lastSrc = normalizeImageUrl(slides[slides.length - 1].dataset.imageSrc || slides[slides.length - 1].querySelector('img')?.src);

    if (firstSrc && secondSrc && firstSrc === beforeLastSrc && secondSrc === lastSrc) {
      return slides.slice(1, -1);
    }

    return slides;
  };

  const getSlideImageUrl = (slide) => {
    const image = slide.querySelector('img');

    return normalizeImageUrl(slide.dataset.imageSrc || image?.currentSrc || image?.src || image?.getAttribute('src'));
  };

  const findLuxTargetIndex = (outer, picker, variant) => {
    const slides = getLuxSlides(outer);
    const colorIndex = getColorOptionIndex(picker, outer);
    const selectedColor = getOptionValue(picker, colorIndex, variant);
    const selectedColorText = normalizeText(selectedColor);
    const selectedColorValueId = getSelectedOptionValueId(picker, colorIndex);
    const expectedGroupName = `option${colorIndex}`;
    const hasGroupedSlides = slides.some((slide) => {
      return slide.dataset.groupName || slide.dataset.groupValue || slide.dataset.groupValueId;
    });

    if (selectedColorValueId) {
      const exactValueIdIndex = slides.findIndex((slide) => {
        return slide.dataset.groupName === expectedGroupName
          && String(slide.dataset.groupValueId || '') === String(selectedColorValueId);
      });

      if (exactValueIdIndex >= 0) return exactValueIdIndex;

      const valueIdOnlyIndex = slides.findIndex((slide) => String(slide.dataset.groupValueId || '') === String(selectedColorValueId));
      if (valueIdOnlyIndex >= 0) return valueIdOnlyIndex;
    }

    if (selectedColorText) {
      const exactGroupIndex = slides.findIndex((slide) => {
        return slide.dataset.groupName === expectedGroupName
          && normalizeText(slide.dataset.groupValue) === selectedColorText;
      });

      if (exactGroupIndex >= 0) return exactGroupIndex;

      const valueOnlyIndex = slides.findIndex((slide) => normalizeText(slide.dataset.groupValue) === selectedColorText);
      if (valueOnlyIndex >= 0) return valueOnlyIndex;

      const aliasGroupIndex = slides.findIndex((slide) => {
        return slide.dataset.groupName === expectedGroupName
          && colorValuesMatch(slide.dataset.groupValue, selectedColor);
      });

      if (aliasGroupIndex >= 0) return aliasGroupIndex;

      const aliasOnlyIndex = slides.findIndex((slide) => colorValuesMatch(slide.dataset.groupValue, selectedColor));
      if (aliasOnlyIndex >= 0) return aliasOnlyIndex;
    }

    if (hasGroupedSlides && selectedColorText) return -1;

    if (!hasGroupedSlides && selectedColorText) {
      const selectedColorKey = normalizeColorKey(selectedColor);
      const imageNameIndex = slides.findIndex((slide) => {
        const image = slide.querySelector('img');
        const imageText = compactText(`${slide.dataset.imageSrc || ''} ${image?.currentSrc || ''} ${image?.src || ''} ${image?.alt || ''}`);

        return imageText.includes(selectedColorKey);
      });

      if (imageNameIndex >= 0) return imageNameIndex;

    }

    const mediaIds = getVariantMediaIds(variant);
    if (mediaIds.size) {
      const mediaIndex = slides.findIndex((slide) => mediaIds.has(String(slide.dataset.mediaId || '')));
      if (mediaIndex >= 0) return mediaIndex;
    }

    const variantImageUrls = getVariantImageUrls(variant);
    if (variantImageUrls.size) {
      const imageIndex = slides.findIndex((slide) => variantImageUrls.has(getSlideImageUrl(slide)));
      if (imageIndex >= 0) return imageIndex;
    }

    return -1;
  };

  const goToLuxIndex = (outer, index) => {
    if (index < 0) return false;

    if (outer.luxSlider?.goToRealIndex) {
      outer.luxSlider.goToRealIndex(index, true);
      return true;
    }

    const dots = outer.querySelectorAll('.lux-dot');
    if (dots[index]) {
      dots[index].click();
      return true;
    }

    const track = outer.querySelector('.lux-track');
    const slides = getLuxSlides(outer);
    const slide = slides[index];

    if (!track || !slide) return false;

    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const offset = index * ((slide.offsetWidth || 0) + gap);

    track.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    track.style.transform = `translateX(-${offset}px)`;

    dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', dotIndex === index);
    });

    return true;
  };

  const syncLuxSliders = (picker, variant) => {
    const sliders = document.querySelectorAll('[data-lux-slider]');
    if (!sliders.length) return;

    sliders.forEach((outer) => {
      const targetIndex = findLuxTargetIndex(outer, picker, variant);
      goToLuxIndex(outer, targetIndex);
    });
  };

  const scheduleLuxSync = (picker, variant, delays = [40, 180, 420, 800, 1200]) => {
    delays.forEach((delay) => {
      window.setTimeout(() => syncLuxSliders(picker, variant), delay);
    });
  };

  const scheduleFormFallback = (picker) => {
    const existing = fallbackTimers.get(picker) || [];
    existing.forEach((timer) => window.clearTimeout(timer));

    const timers = [350, 900].map((delay) => window.setTimeout(() => {
      const variant = getSelectedVariant(picker);
      if (variant) updateForm(picker, variant, true);
    }, delay));

    fallbackTimers.set(picker, timers);
  };

  const setupPicker = (picker) => {
    if (initializedPickers.has(picker)) return;

    initializedPickers.add(picker);

    const variants = parseVariantData(picker);
    if (!variants.length) return;

    const initialId = picker.getAttribute('selected-variant');
    const initial = findVariantById(picker, initialId)
      || variants.find((variant) => variant.available)
      || variants[0];

    updateForm(picker, initial, false);
    scheduleLuxSync(picker, initial, [80, 300]);

    picker.addEventListener('change', (event) => {
      const target = event.target;
      if (!target?.matches?.('input[is-value], input[type="radio"][is-value], input[type="hidden"][is-value], hdt-richlist button, button[aria-selected]')) return;

      scheduleLuxSync(picker, getSelectedVariant(picker));
      scheduleFormFallback(picker);
    });

    picker.addEventListener('click', (event) => {
      const target = event.target;
      if (!target?.closest?.('label, button')) return;

      window.setTimeout(() => scheduleLuxSync(picker, getSelectedVariant(picker), [30, 200]), 0);
    });
  };

  const setupAll = (scope = document) => {
    scope.querySelectorAll('hdt-variant-picker').forEach(setupPicker);
  };

  const handleVariantEvent = (event) => {
    const picker = getPickerFromEvent(event);
    if (!picker) return;

    const variant = getVariantFromEvent(event, picker) || getSelectedVariant(picker);
    if (!variant) return;

    updateForm(picker, variant, false);
    scheduleLuxSync(picker, variant);
  };

  const handlePickerInteraction = (event) => {
    const picker = event.target?.closest?.('hdt-variant-picker');
    if (!picker) return;

    window.setTimeout(() => {
      const variant = getSelectedVariant(picker);
      if (variant) updateForm(picker, variant, true);
      scheduleLuxSync(picker, variant, [80, 260, 600, 1000, 1500]);
    }, 0);
  };

  ['variant:change', 'variant:changed', 'variant:updated', 'product:variant-change'].forEach((eventName) => {
    document.addEventListener(eventName, handleVariantEvent);
  });

  document.addEventListener('change', handlePickerInteraction, true);
  document.addEventListener('click', handlePickerInteraction, true);

  document.addEventListener('lux:ready', () => {
    document.querySelectorAll('hdt-variant-picker').forEach((picker) => {
      scheduleLuxSync(picker, getSelectedVariant(picker) || findVariantById(picker, picker.getAttribute('selected-variant')), [0]);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setupAll(), { once: true });
  } else {
    setupAll();
  }

  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));

  const syncStickyVariant = () => {
    const source = document.getElementById('lux-variant-id');
    const stickyInput = document.querySelector('.hdt-sticky-atc__form input[name="id"]');
    if (source?.value && stickyInput) stickyInput.value = source.value;
  };

  document.addEventListener('change', syncStickyVariant, true);
  ['variant:change', 'variant:changed', 'variant:updated', 'product:variant-change'].forEach((eventName) => {
    document.addEventListener(eventName, () => window.setTimeout(syncStickyVariant, 0));
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncStickyVariant, { once: true });
  } else {
    syncStickyVariant();
  }
})();
